import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// Custom hook for real-time subscriptions
export const useRealtime = () => {
  const [connections, setConnections] = useState({});
  const { user } = useAuth();

  // Subscribe to order changes for real-time notifications
  const subscribeToOrders = useCallback((callback, filters = {}) => {
    if (!user) return null;

    const channelName = `orders_${user?.id}_${Date?.now()}`;
    
    let channel = supabase?.channel(channelName);

    // Subscribe to order changes
    channel = channel?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: filters?.car_house_id ? `car_house_id=eq.${filters?.car_house_id}` : undefined
      },
      (payload) => {
        callback?.(payload);
      }
    );

    // Subscribe to order assignments if user is a technician
    if (filters?.technician_id) {
      channel = channel?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `assigned_technician_id=eq.${filters?.technician_id}`
        },
        (payload) => {
          // Only trigger for assignment changes
          if (payload?.new?.assigned_technician_id !== payload?.old?.assigned_technician_id) {
            callback?.({ 
              ...payload, 
              eventType: 'technician_assigned' 
            });
          }
        }
      );
    }

    channel?.subscribe((status) => {
    });

    // Store connection for cleanup
    setConnections(prev => ({
      ...prev,
      [channelName]: channel
    }));

    return () => {
      supabase?.removeChannel(channel);
      setConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections?.[channelName];
        return newConnections;
      });
    };
  }, [user]);

  // Subscribe to notifications for current user
  const subscribeToNotifications = useCallback((callback) => {
    if (!user) return null;

    const channelName = `notifications_${user?.id}`;
    
    let channel = supabase?.channel(channelName)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user?.id}`
      },
      (payload) => {
        callback?.(payload?.new);
      }
    )?.subscribe((status) => {
    });

    // Store connection for cleanup
    setConnections(prev => ({
      ...prev,
      [channelName]: channel
    }));

    return () => {
      supabase?.removeChannel(channel);
      setConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections?.[channelName];
        return newConnections;
      });
    };
  }, [user]);

  // Subscribe to order service items changes
  const subscribeToOrderServiceItems = useCallback((orderId, callback) => {
    if (!orderId) return null;

    const channelName = `order_service_items_${orderId}`;
    
    let channel = supabase?.channel(channelName)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'order_service_items',
        filter: `order_id=eq.${orderId}`
      },
      (payload) => {
        callback?.(payload);
      }
    )?.subscribe((status) => {
    });

    // Store connection for cleanup
    setConnections(prev => ({
      ...prev,
      [channelName]: channel
    }));

    return () => {
      supabase?.removeChannel(channel);
      setConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections?.[channelName];
        return newConnections;
      });
    };
  }, []);

  // Subscribe to attachments changes for an order
  const subscribeToOrderAttachments = useCallback((orderId, callback) => {
    if (!orderId) return null;

    const channelName = `attachments_${orderId}`;
    
    let channel = supabase?.channel(channelName)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'attachments',
        filter: `order_id=eq.${orderId}`
      },
      (payload) => {
        callback?.(payload);
      }
    )?.subscribe((status) => {
    });

    // Store connection for cleanup
    setConnections(prev => ({
      ...prev,
      [channelName]: channel
    }));

    return () => {
      supabase?.removeChannel(channel);
      setConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections?.[channelName];
        return newConnections;
      });
    };
  }, []);

  // Subscribe to audit log changes for an order
  const subscribeToOrderAuditLogs = useCallback((orderId, callback) => {
    if (!orderId) return null;

    const channelName = `audit_logs_${orderId}`;
    
    let channel = supabase?.channel(channelName)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'audit_logs',
        filter: `order_id=eq.${orderId}`
      },
      (payload) => {
        callback?.(payload?.new);
      }
    )?.subscribe((status) => {
    });

    // Store connection for cleanup
    setConnections(prev => ({
      ...prev,
      [channelName]: channel
    }));

    return () => {
      supabase?.removeChannel(channel);
      setConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections?.[channelName];
        return newConnections;
      });
    };
  }, []);

  // Subscribe to user profile changes (for current user)
  const subscribeToUserProfile = useCallback((callback) => {
    if (!user) return null;

    const channelName = `user_profile_${user?.id}`;
    
    let channel = supabase?.channel(channelName)?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${user?.id}`
      },
      (payload) => {
        callback?.(payload?.new);
      }
    )?.subscribe((status) => {
    });

    // Store connection for cleanup
    setConnections(prev => ({
      ...prev,
      [channelName]: channel
    }));

    return () => {
      supabase?.removeChannel(channel);
      setConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections?.[channelName];
        return newConnections;
      });
    };
  }, [user]);

  // Presence tracking for technicians (who's online)
  const subscribeToTechnicianPresence = useCallback((callback) => {
    if (!user) return null;

    const channelName = `technician_presence`;
    
    let channel = supabase?.channel(channelName, {
      config: {
        presence: {
          key: user?.id
        }
      }
    });

    // Track presence
    channel
      ?.on('presence', { event: 'sync' }, () => {
        const presenceState = channel?.presenceState();
        const technicians = Object?.entries(presenceState)?.map(([userId, presence]) => ({
          user_id: userId,
          ...presence?.[0]
        }));
        callback?.({ event: 'sync', technicians });
      })
      ?.on('presence', { event: 'join' }, ({ newPresences }) => {
        callback?.({ event: 'join', technicians: newPresences });
      })
      ?.on('presence', { event: 'leave' }, ({ leftPresences }) => {
        callback?.({ event: 'leave', technicians: leftPresences });
      })
      ?.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Send current user's presence data
          await channel?.track({
            user_id: user?.id,
            online_at: new Date()?.toISOString(),
            status: 'online'
          });
        }
      });

    // Store connection for cleanup
    setConnections(prev => ({
      ...prev,
      [channelName]: channel
    }));

    return () => {
      channel?.untrack();
      supabase?.removeChannel(channel);
      setConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections?.[channelName];
        return newConnections;
      });
    };
  }, [user]);

  // Cleanup all connections on unmount or user change
  useEffect(() => {
    return () => {
      Object?.values(connections)?.forEach(channel => {
        if (channel) {
          supabase?.removeChannel(channel);
        }
      });
      setConnections({});
    };
  }, [user]);

  // Cleanup connections when user changes
  useEffect(() => {
    if (!user) {
      Object?.values(connections)?.forEach(channel => {
        if (channel) {
          supabase?.removeChannel(channel);
        }
      });
      setConnections({});
    }
  }, [user, connections]);

  return {
    subscribeToOrders,
    subscribeToNotifications,
    subscribeToOrderServiceItems,
    subscribeToOrderAttachments,
    subscribeToOrderAuditLogs,
    subscribeToUserProfile,
    subscribeToTechnicianPresence,
    activeConnections: Object?.keys(connections)?.length
  };
};

export default useRealtime;